// @flow

import React, { type ElementRef, forwardRef } from 'react';

import { kebabCaseAriaAttrs } from '@zapier/design-system';
import { useLink } from '@zapier/common-components';

/**
 * Array of predicate functions indicating that a `url` cannot be navigated to.
 * Predicate should return `true` if the URL *cannot* be navigated to.
 */
const unnavigableUrlPredicates = [url => url.startsWith('mailto')];

const isNavigableUrl = (url: string): boolean =>
  unnavigableUrlPredicates.every(predicate => !predicate(url));

const RouterLinkWrapper = forwardRef(function(props: any, ref?: ElementRef<*>) {
  // Use LinkProvider to hook into client side routing
  const { routeTo } = useLink();

  const trimmedHref = props.href ? props.href.trim() : props.href;

  const handleClick = (event: SyntheticMouseEvent<HTMLAnchorElement>) => {
    // Handle onClick if given
    if (props.onClick) {
      props.onClick(event);
    }

    // If this link goes to a target, skip client-side routing.
    if (props.target) return;

    if (
      typeof routeTo === 'function' &&
      trimmedHref &&
      isNavigableUrl(trimmedHref)
    ) {
      // Send to client-side routing
      routeTo(trimmedHref, { event });
      // Stop browser from handling routing since routeTo is taking over
      event.preventDefault();
    }
  };

  return (
    // This will receive all a11y props from the Link props
    // eslint-disable-next-line
    <a {...kebabCaseAriaAttrs(props)} onClick={handleClick} ref={ref}>
      {props.children}
    </a>
  );
});

export default RouterLinkWrapper;
